import { PaymentModuleId } from '@foundation/apis/payments';

import {
  PaymentIntentFlow,
  PaymentIntentStatus,
} from '@/schemas/PaymentIntent';
import { registerStartupable } from '@/services/base/StartupHelper';
import { getPaymentIntentStatusById } from '@/services/payments/intent/PaymentIntentApi';
import { loadScript } from '@/utils/loadScript';

import { getManageNuveiConfiguration, getNuveiConfiguration } from './NuveiApi';
import { NuveiConfigurationApiResponse } from './NuveiConfigurationApiResponse';
import { NuveiCardCreatePaymentInput } from './NuveiCreatePaymentInput';
import { NuveiTransactionResponse as NuveiTransactionResponse } from './NuveiTransactionResponse';
import { OptionalStartupable } from '../base/Startupable';
import PaymentStore from '../payments/PaymentStore';

export const NUVEI_CARDS_CANCEL_ERROR_CODE = '1156';
export const NUVEI_TWINT_CANCEL_ERROR_CODE = '9072';

const PAYMENT_STATUS_POLL_INTERVAL_MS = 3_000;
// The backend waits 15 seconds before confirming Nuvei authorization so a late decline can supersede it.
const PAYMENT_STATUS_CONFIRMATION_WINDOW_MS = 16_000;
const SUCCESSFUL_PAYMENT_STATUSES = new Set([
  PaymentIntentStatus.Authorized,
  PaymentIntentStatus.Settled,
]);
const FAILED_PAYMENT_STATUSES = new Set([
  PaymentIntentStatus.ProcessorDeclined,
  PaymentIntentStatus.GatewayRejected,
  PaymentIntentStatus.Failed,
  PaymentIntentStatus.AuthorizationExpired,
  PaymentIntentStatus.SettlementDeclined,
]);

interface NuveiSafeCharge {
  readonly createPayment: (
    options: NuveiCardCreatePaymentInput,
    handler: (params: NuveiTransactionResponse) => void,
  ) => Promise<NuveiTransactionResponse>;

  fields: (options: { fonts: FontOptions[]; locale: string }) => FieldBuilder;
}

interface FontOptions {
  cssUrl: string;
}

interface FieldBuilder {
  create: (
    type: 'card' | 'ccNumber' | 'ccExpiration' | 'ccCvc',
    config?: unknown,
  ) => unknown;
}

declare global {
  interface Window {
    readonly SafeCharge: (argument: unknown) => NuveiSafeCharge;
  }
}

class NuveiCardService implements OptionalStartupable {
  private _nuveiCardConfiguration!: NuveiConfigurationApiResponse;
  private _safeCharge!: NuveiSafeCharge;

  public readonly name = 'NuveiCardService';
  public readonly moduleId = PaymentModuleId.NuveiCreditCard;

  constructor() {
    registerStartupable(this, [PaymentStore]);
  }

  public async startup(): Promise<void> {
    /*For Nuvei it is important to regenerate clientUniqueId every time we do attempt to pay, so API call to Payment service should be done on every button click */
  }

  public shouldStart(): boolean {
    const isAllowed = PaymentStore.allowedPaymentModules.includes(
      this.moduleId,
    );

    const hasAtLeastOneInstrument =
      PaymentStore.instruments.find((i) => i.moduleId === this.moduleId) !==
      undefined;

    return isAllowed || hasAtLeastOneInstrument;
  }

  private async initTransaction(
    paymentInstrumentId?: string,
    customerNumber?: string,
    accessToken?: string,
  ): Promise<void> {
    let nuveiCardConfiguration;
    if (PaymentStore.flow === PaymentIntentFlow.Manage) {
      if (customerNumber === undefined) {
        throw new Error('customerNumber is undefined!');
      }

      if (PaymentStore.legalEntity === undefined) {
        throw new Error('Legal entity cannot be undefined');
      }

      nuveiCardConfiguration = await getManageNuveiConfiguration(
        PaymentModuleId.NuveiCreditCard,
        PaymentStore.legalEntity,
        customerNumber,
        accessToken ?? '',
      );
    } else {
      nuveiCardConfiguration = await getNuveiConfiguration(
        PaymentStore.paymentIntentId,
        PaymentModuleId.NuveiCreditCard,
        accessToken ?? '',
        paymentInstrumentId,
      );
    }

    if (nuveiCardConfiguration === undefined) {
      throw new Error('Nuvei card transaction configuration has no value!');
    }
    this._nuveiCardConfiguration = nuveiCardConfiguration;
  }

  private initSafeCharge(): Promise<void> {
    return loadScript(
      'https://cdn.safecharge.com/safecharge_resources/v1/websdk/safecharge.js',
    ).then(() => {
      this._safeCharge = window.SafeCharge({
        sessionToken: this._nuveiCardConfiguration.sessionToken,
        env: this._nuveiCardConfiguration.environment,
        merchantId: this._nuveiCardConfiguration.merchantId,
        merchantSiteId: this._nuveiCardConfiguration.merchantSiteId,
      });
    });
  }

  public async createPayment(
    cardNumberField: unknown,
    cardHolderName: string,
  ): Promise<NuveiTransactionResponse> {
    return new Promise((resolve, reject) => {
      this._safeCharge.createPayment(
        {
          sessionToken: this._nuveiCardConfiguration.sessionToken,
          cardHolderName: cardHolderName,
          paymentOption: cardNumberField,
          billingAddress: {
            email: PaymentStore.customerEmail,
            country: PaymentStore.customerCountryCode,
          },
          alwaysCollectCvv: 'optional',
        },
        (response) => {
          this.createPaymentResponseHandler(response, reject, resolve);
        },
      );
    });
  }

  public async createCITPayment(
    customerNumber: string | undefined,
  ): Promise<void> {
    if (customerNumber === undefined) {
      throw new Error('customerNumber is undefined!');
    }

    return new Promise((resolve, reject) => {
      this._safeCharge.createPayment(
        {
          sessionToken: this._nuveiCardConfiguration.sessionToken,
          userTokenId: customerNumber,
          paymentOption: {
            userPaymentOptionId:
              this._nuveiCardConfiguration.userPaymentOptionId,
          },
          billingAddress: {
            email: PaymentStore.customerEmail,
            country: PaymentStore.customerCountryCode,
          },
          alwaysCollectCvv: 'optional',
          //deviceDetails - not provided
        },
        (response) => {
          this.createPaymentResponseHandlerForCIT(response)
            .then(resolve)
            .catch(reject);
        },
      );
    });
  }

  public async initTransactionAndSafeCharge(
    instrumentId?: string,
    customerNumber?: string,
    accessToken?: string,
  ): Promise<NuveiSafeCharge> {
    await this.initTransaction(instrumentId, customerNumber, accessToken);
    await this.initSafeCharge();

    return Promise.resolve(this._safeCharge);
  }

  public async confirmPayment(
    paymentIntentId: string,
    accessToken: string,
  ): Promise<void> {
    const confirmationDeadline =
      Date.now() + PAYMENT_STATUS_CONFIRMATION_WINDOW_MS;
    let status: PaymentIntentStatus;

    do {
      status = await getPaymentIntentStatusById(paymentIntentId, accessToken);

      const remainingTime = confirmationDeadline - Date.now();
      if (remainingTime > 0) {
        await new Promise((resolve) =>
          window.setTimeout(
            resolve,
            Math.min(PAYMENT_STATUS_POLL_INTERVAL_MS, remainingTime),
          ),
        );
      }
    } while (Date.now() < confirmationDeadline);

    if (!SUCCESSFUL_PAYMENT_STATUSES.has(status)) {
      if (FAILED_PAYMENT_STATUSES.has(status)) {
        throw new Error(`Payment failed with status '${status}'.`);
      }

      throw new Error(`Payment is not confirmed. Status: '${status}'.`);
    }
  }

  public isNuveiTransactionUnsuccessful(
    convertedError: NuveiTransactionResponse,
  ): boolean {
    return (
      convertedError.result === 'ERROR' ||
      convertedError.result === 'DECLINED' ||
      convertedError.cancelled === true ||
      convertedError.cancelled === 'true'
    );
  }

  public isNuveiTransactionCancelledByUser(
    response: NuveiTransactionResponse,
  ): boolean {
    return (
      response.errCode === NUVEI_CARDS_CANCEL_ERROR_CODE ||
      response.cancelled === true ||
      response.cancelled === 'true'
    );
  }

  public createPaymentResponseHandler(
    params: NuveiTransactionResponse,
    reject: (params: NuveiTransactionResponse) => void,
    resolve: (params: NuveiTransactionResponse) => void,
  ): void {
    const responseWithConvertedError: NuveiTransactionResponse = {
      ...params,
      errCode:
        typeof params.errCode === 'string'
          ? params.errCode
          : `${params.errCode}`,
    };

    if (this.isNuveiTransactionUnsuccessful(responseWithConvertedError)) {
      reject(responseWithConvertedError);
    } else {
      resolve(responseWithConvertedError);
    }
  }

  public createPaymentResponseHandlerForCIT(
    params: NuveiTransactionResponse,
  ): Promise<void> {
    const responseWithConvertedError: NuveiTransactionResponse = {
      ...params,
      errCode:
        typeof params.errCode === 'string'
          ? params.errCode
          : `${params.errCode}`,
    };

    return this.isNuveiTransactionUnsuccessful(responseWithConvertedError)
      ? Promise.reject(responseWithConvertedError) // cancel, failure?
      : Promise.resolve(); // success
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  public isNuveiError(obj: any): obj is NuveiTransactionResponse {
    return (
      obj &&
      typeof obj === 'object' &&
      'result' in obj &&
      this.isNuveiTransactionUnsuccessful(obj)
    );
  }
}

export default new NuveiCardService();
